import type { Type } from '@angular/core';
import type { ZodMiniType } from 'zod/mini';
import type { DashboardQueryColumn } from '@h20/contracts/types/api/dashboard/DashboardData';
import type { DashboardQueryKey } from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import type { DashboardLayout } from '../dashboard.types';
import type { DashboardComponentKey } from './dashboard-component-types';

/** One cell of a saved board layout. */
export type DashboardLayoutCell = NonNullable<DashboardLayout['components']>[number];

/** What a widget may look at while building its settings fields. */
export type DashboardWidgetSettingsContext = {
    /** The cell's stored `widgetConfig`. */
    readonly config: unknown;
    /** The catalogue query the cell is bound to, if any. */
    readonly queryKey: DashboardQueryKey | undefined;
    /** The columns that query declares. Empty while the cell is unbound. */
    readonly columns: readonly DashboardQueryColumn[];
};

export type DashboardWidgetRegistration<TConfig = unknown> = {
    readonly component: Type<unknown>;
    /** i18n key for the widget's own entry in the "Add component" sidebar. */
    readonly sidebarLabel?: string;
    /** Whether the settings panel shows the title and caption fields (default `true`). */
    readonly hasTitle?: boolean;
    /** Whether the cell binds this widget to a catalogue query. Defaults to `true`. */
    readonly dataBound?: boolean;
    /** The widget's config schema */
    readonly configSchema?: ZodMiniType<TConfig>;
    /** An old component type whose saved cells this widget renders, reading them through its config schema. */
    readonly replaces?: DashboardComponentKey;
};

/** Widgets by the key a layout names them with. */
export type DashboardWidgetPool = Readonly<Record<string, DashboardWidgetRegistration>>;

const widgets = new Map<string, DashboardWidgetRegistration>();

/** Registers a widget under a key. Throws if the key already points at a different component. */
export function registerWidget(key: string, registration: DashboardWidgetRegistration): void {
    const existing = widgets.get(key);
    if (existing && existing.component !== registration.component) {
        throw new Error(
            `pulse-dashboard: widget key '${key}' is already registered to a different component.`
        );
    }
    widgets.set(key, registration);
}

/** Looks up a registered widget. */
export function findWidget(key: string): DashboardWidgetRegistration | undefined {
    return widgets.get(key);
}

/** The key of the widget that replaces an old component type. */
export function findReplacingWidget(componentType: string): string | undefined {
    for (const [widgetKey, widget] of widgets) {
        if (widget.replaces === componentType) {
            return widgetKey;
        }
    }
    return undefined;
}

/** Every key registered so far, for the authoring dropdown. */
export function registeredWidgetKeys(): string[] {
    return [...widgets.keys()];
}
